import Axios, { AxiosError, AxiosResponse } from 'axios'
import ApiService, {
    apiErrorText,
    IRequestParameters,
    readApiErrorBody,
} from '@/plugins/api-plugin/services/api.service'
import ForSureClientDao from '@/interfaces/admin/ForSureClient.dao'
import ForSureClientLookupsDao from '@/interfaces/admin/ForSureClientLookups.dao'
import { CreateForSureClientDao, UpdateForSureClientDao } from '@/interfaces/admin/WriteForSureClient.dao'
import { PageNumberPagination } from '@/interfaces/common/PageNumberPagination'

export type ForSureOrdersExportResult =
    | { ok: true; response: AxiosResponse<Blob> }
    | { ok: false; cancelled: boolean; status: number | null; message: string }

/** The Nest error message of a failed request as one line, read from a Blob body as well. */
export const readApiErrorMessage = async (data: unknown): Promise<string> =>
    apiErrorText((await readApiErrorBody(data))?.message)

export default class ForSureClientsService extends ApiService {
    async getForSureClients(params?: IRequestParameters): Promise<[ForSureClientDao[], PageNumberPagination] | null> {
        return this.request<undefined, [ForSureClientDao[], PageNumberPagination] | null>(
            'GET',
            '/forsure-clients',
            params,
            undefined,
            undefined,
            'getForSureClients',
        ).catch(() => null)
    }

    async getForSureClientLookups(): Promise<ForSureClientLookupsDao | null> {
        return this.request<undefined, ForSureClientLookupsDao>(
            'GET',
            '/forsure-clients/lookups',
            undefined,
            undefined,
            undefined,
            'getForSureClientLookups',
        ).catch(() => null)
    }

    async getForSureClient(id: number): Promise<ForSureClientDao | null> {
        return this.request<undefined, ForSureClientDao>(
            'GET',
            `/forsure-clients/${id}`,
            undefined,
            undefined,
            undefined,
            'getForSureClient',
        ).catch(() => null)
    }

    async createForSureClient(dto: CreateForSureClientDao): Promise<ForSureClientDao | null> {
        return this.request<CreateForSureClientDao, ForSureClientDao>(
            'POST',
            '/forsure-clients',
            undefined,
            dto,
            undefined,
            'createForSureClient',
        ).catch(() => null)
    }

    async updateForSureClient(id: number, dto: UpdateForSureClientDao): Promise<ForSureClientDao | null> {
        return this.request<UpdateForSureClientDao, ForSureClientDao>(
            'PUT',
            `/forsure-clients/${id}`,
            undefined,
            dto,
            undefined,
            'updateForSureClient',
        ).catch(() => null)
    }

    async deleteForSureClient(id: number): Promise<{ success: true } | null> {
        return this.request<undefined, { success: true }>(
            'DELETE',
            `/forsure-clients/${id}`,
            undefined,
            undefined,
            undefined,
            'deleteForSureClient',
        ).catch(() => null)
    }

    /**
     * GET /forsure-clients/{id}/orders-export - xlsx bulk-import file for ForSure (order backdating).
     * `id` is the ForSure configuration id (ForSureClients.Id), not Client_Id.
     * `fromDate` (YYYY-MM-DD) overrides every enabled channel account's SubmissionStartDate.
     * The global error alert is suppressed: 404 means "nothing to export" and is a message, not a failure.
     */
    async exportForSureClientOrders(id: number, fromDate?: string): Promise<ForSureOrdersExportResult> {
        return this.request<undefined, AxiosResponse<Blob>>(
            'GET',
            `/forsure-clients/${id}/orders-export`,
            fromDate ? { fromDate } : undefined,
            undefined,
            'blob',
            'exportForSureClientOrders',
            { suppressErrorAlert: true },
        )
            .then((response) => ({ ok: true as const, response }))
            .catch((reason: AxiosError) => this.toExportFailure(reason))
    }

    private async toExportFailure(reason: AxiosError): Promise<ForSureOrdersExportResult> {
        if (Axios.isCancel(reason)) {
            return { ok: false, cancelled: true, status: null, message: '' }
        }
        const status = reason?.response?.status ?? null
        const message = (await readApiErrorMessage(reason?.response?.data)) || reason?.message || 'Export failed'
        return { ok: false, cancelled: false, status, message }
    }
}
